import { useBlockAppearanceStyles } from '@unifyapps/carbon/no-code/hooks/useBlockAppearanceStyles';
import { convertExpoGradientToCss } from '@unifyapps/carbon/utils/gradients';
import useCustomStyles from '@unifyapps/carbon/utils/useCustomStyles';
import type { StackAppearance } from '@unifyapps/defs/blocks/Stack/types';
import { useMemo } from 'react';
import type { StackProps } from './BaseStack';
import { getWebBlurStyles } from './getWebBlurStyles';

type UseStackBaseStylesParams = Pick<StackProps, 'block' | 'registry'> & {
  appearance: StackAppearance | undefined;
};

// Setup shared by BaseStack and EffectsStack. Returns the style pieces, not a
// finished `mergedStyle`, so each caller composes its own: BaseStack merges the
// three returned sources, EffectsStack layers `effectsStyle` + stacking context
// on top. Keeping the merge in the caller is what lets the effects path stay out
// of BaseStack entirely (zero-overhead split).
export function useStackBaseStyles({ appearance, block, registry }: UseStackBaseStylesParams) {
  const { className, style } = useBlockAppearanceStyles({ appearanceStyles: appearance?.styles });

  const { finalStyle, gradientConfig, blurConfig } = useCustomStyles({
    customCSS: block?.additional?.customCSS,
    customStyles: block.component?.appearance?.customStyles,
    style,
  });

  const linearGradientStyle = useMemo(
    () => convertExpoGradientToCss(gradientConfig),
    [gradientConfig],
  );
  const blurStyle = useMemo(() => getWebBlurStyles(blurConfig), [blurConfig]);

  const wrapClassName = appearance?.wrapContent ? 'flex-wrap' : 'flex-nowrap';
  const BlockRenderer = registry.getBlockRenderer();

  return { className, wrapClassName, finalStyle, blurStyle, linearGradientStyle, BlockRenderer };
}
